import { z } from "zod"

import {
  storageConnectionErrorCodes,
  storageConnectionStatuses,
  storageProviders,
  storageSyncErrorCodes,
} from "../../storage.js"

export const StorageProviderSchema = z.enum(storageProviders)

export const StorageConnectionSchema = z.object({
  id: z.string().uuid(),
  provider: StorageProviderSchema,
  accountEmail: z.string(),
  rootFolderName: z.string(),
  rootFolderUrl: z.string().url(),
  /** The folder copies go to sits in a Shared Drive, not My Drive.
      Defaulted so a web build that deploys before the API still parses. */
  rootFolderInSharedDrive: z.boolean().default(false),
  status: z.enum(storageConnectionStatuses),
  errorCode: z.enum(storageConnectionErrorCodes).nullable(),
  connectedAt: z.string().datetime(),
})

export const StorageConnectionResponseSchema = z.object({
  configured: z.object({ google_drive: z.boolean() }),
  /** The Drive Picker is set up here, so the firm can choose the folder.
      Defaulted so a web build that deploys before the API still parses. */
  pickerAvailable: z.boolean().default(false),
  connection: StorageConnectionSchema.nullable(),
  counts: z.object({
    uploaded: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  canCopyEarlier: z.boolean(),
})

export type ApiStorageConnectionResponse = z.infer<
  typeof StorageConnectionResponseSchema
>
export type ApiStorageConnection = z.infer<typeof StorageConnectionSchema>

/** What the Drive Picker needs in the browser. The access token is short
 * lived; the refresh token never leaves the API. */
export const StoragePickerSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.string().datetime(),
  apiKey: z.string().min(1),
  appId: z.string().min(1),
})

export type ApiStoragePickerSession = z.infer<
  typeof StoragePickerSessionResponseSchema
>

export const StorageRootRequestSchema = z.object({
  folderId: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\w-]+$/, "A Drive folder id"),
})

export const StorageAuthorizeResponseSchema = z.object({
  authorizeUrl: z.string().url(),
})

export const StorageFailureSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  fileName: z.string(),
  sentAt: z.string().datetime(),
  reason: z.enum(storageSyncErrorCodes).nullable(),
})

export const StorageFailuresResponseSchema = z.object({
  items: z.array(StorageFailureSchema),
  nextCursor: z.string().nullable(),
})

export type ApiStorageFailure = z.infer<typeof StorageFailureSchema>
export type ApiStorageFailuresResponse = z.infer<
  typeof StorageFailuresResponseSchema
>

export const StorageRetryResponseSchema = z.object({
  retried: z.number().int().nonnegative(),
})
